import type { AuditEntry } from "../types";

type TFn = ((key: string, values?: Record<string, string | number | Date>) => string) & {
  has?: (key: string) => boolean;
};

export function resolveResourceTypeLabel(rawResourceType: string, t: TFn): string {
  const labelKey = `audit.resource_types.${rawResourceType}` as Parameters<typeof t>[0];
  if (typeof t.has === "function" && !t.has(labelKey)) {
    return rawResourceType;
  }
  const label = t(labelKey);
  return label === labelKey ? rawResourceType : label;
}

export function resolveAuditResourceLabel(entry: AuditEntry, t: TFn): string {
  if (entry.resource?.name) return entry.resource.name;

  const args = entry.args ?? {};
  for (const key of ["file_name", "folder_name", "group_name", "provider_name", "title", "slug", "name"]) {
    const value = args[key];
    if (typeof value === "string" && value.trim()) return value;
  }

  return entry.resourceType ? resolveResourceTypeLabel(entry.resourceType, t) : "";
}

export function resolveAuditResourceSubtitle(entry: AuditEntry, t: TFn): string {
  const typeLabel = entry.resourceType ? resolveResourceTypeLabel(entry.resourceType, t) : "";
  if (entry.resource?.subtitle) {
    return typeLabel ? `${typeLabel} · ${entry.resource.subtitle}` : entry.resource.subtitle;
  }
  return typeLabel;
}

export function resolveAuditOrgLabel(entry: AuditEntry, fallback: string): string {
  return entry.org?.name || fallback;
}

export function resolveAuditPrincipalLabel(entry: AuditEntry): string {
  if (entry.principal?.name) return entry.principal.name;
  const args = entry.args ?? {};
  for (const key of ["principal_name", "principalName", "member_email", "user_email", "email"]) {
    const value = args[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  return "";
}

export function resolveSourceLabel(source: string | null | undefined, t: TFn): string {
  if (!source) return "";
  const labelKey = `audit.sources.${source}` as Parameters<typeof t>[0];
  if (typeof t.has === "function" && !t.has(labelKey)) {
    return source;
  }
  const label = t(labelKey);
  return label === labelKey ? source : label;
}
